import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "../../../../misc/Loader";
import { List, ListItemButton, ListItemText, TextField, useTheme } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { css } from "@emotion/css";
import { grey } from "@mui/material/colors";
import { first } from "nate-react-api-helpers";
import { CategoryContext } from "./CategoryProvider";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function walk(nd, cb) { cb(nd); nd.children.forEach(c => { walk(c, cb); }); }
export function CategoryTree(props) {
    const mode = props.mode;
    const catCtx = useContext(CategoryContext);
    const result = useMemo(() => catCtx.categories.filter(c => !c.hideInProductEditor), [catCtx.categories]);
    const [selected, setSelected] = useState();
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const tree = useMemo(() => { const map = result.filter(r => mode === null || r.kind === mode).reduce((acc, r) => { acc[r.id] = Object.assign({}, r, { children: [] }); return acc; }, {}); const root = { children: [] }; for (let i in map) {
        const el = map[i];
        if (!el.parentCategory) {
            root.children.push(el);
            continue;
        }
        map[el.parentCategory].children.push(el);
        el.parent = map[el.parentCategory];
    } walk(root, nd => { nd.children.sort((a, b) => { if (a.sortOrder === null) {
        if (b.sortOrder === null)
            return 0;
        return 1;
    } if (b.sortOrder === null)
        return -1; return a.sortOrder - b.sortOrder; }); }); return root; }, [result, mode]);
    const onChange = props.onChange;
    const propValue = props.value;
    const loaded = result.length > 0 && tree.children.length > 0;
    useEffect(() => {
        if (!loaded)
            return;
        if (!propValue)
            return;
        let nodes = [tree];
        while (nodes.length > 0) {
            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];
                const find = first(node.children, c => c.id === propValue);
                if (find) {
                    setSelected(find);
                    selectedRef.current = find;
                    return;
                }
            }
            let nextNodes = [];
            nodes.map(n => nextNodes.push(...n.children));
            nodes = nextNodes;
        } // clear parent result because it doesn't exist
        onChange(null);
    }, [propValue, tree, loaded, onChange]);
    const onCategoryName = props.onCategoryName;
    useEffect(() => {
        if (!loaded)
            return; // use ref b/c of race with useEffect above
        const selected = selectedRef.current;
        if (!selected) {
            onChange(null);
            return;
        }
        let cursor = selected;
        let level = 0;
        let list = [];
        while (cursor) {
            list.push(cursor);
            cursor = cursor.parent;
            level++;
        }
        onChange(selected.id, { level: level, isAutoQtyHinge: list.filter(l => (l === null || l === void 0 ? void 0 : l.name.toLowerCase().indexOf("hinge")) !== -1 && (l === null || l === void 0 ? void 0 : l.name.toLowerCase().indexOf("continuous")) === -1).length > 0 });
        if (onCategoryName)
            onCategoryName(selected.name);
    }, [selected, onChange, onCategoryName, propValue, loaded]);
    const [search, setSearch] = useState("");
    return /*#__PURE__*/ _jsx(Loader, Object.assign(Object.assign({}, catCtx), { children: () => { return /*#__PURE__*/ _jsxs("div", { style: { flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }, children: [/*#__PURE__*/ _jsxs(List, { dense: true, className: listClass, style: { flex: 1, overflow: "auto" }, children: [/*#__PURE__*/ _jsxs(ListItemButton, { className: selectedCategoryClass, onClick: () => setSelected(undefined), children: [/*#__PURE__*/ _jsx(ListItemText, { primary: "Category" }), /*#__PURE__*/ _jsx(ExpandMoreIcon, {})] }), search === "" ? /*#__PURE__*/ _jsx(_Fragment, { children: /*#__PURE__*/ _jsx(Item, { selected: selected, tree: tree, setSelected: setSelected }) }) : /*#__PURE__*/ _jsx(_Fragment, { children: tree.children.map(c => /*#__PURE__*/ _jsx(SearchItem, { node: c, search: search, selected: selected, onSelect: setSelected })) })] }), /*#__PURE__*/ _jsx("div", { style: { height: 8 } }), /*#__PURE__*/ _jsx(TextField, { fullWidth: true, size: "small", label: "Search", value: search, onChange: e => setSearch(e.target.value) })] }); } }));
}
function SearchItem(props) { const match = useMemo(() => { if (props.node.name.toLowerCase().indexOf(props.search.toLowerCase()) !== -1)
    return "root"; const matches = function (n) { if (n.name.toLowerCase().indexOf(props.search.toLowerCase()) !== -1)
    return true; return n.children.some(c => matches(c)); }; return matches(props.node); }, [props.node, props.search]); if (match === false && !props.showAll) {
    return null;
} return /*#__PURE__*/ _jsxs(_Fragment, { children: [props.node.children.length > 0 ? /*#__PURE__*/ _jsx(ParentCategoryItem, { value: props.node, onClick: () => { if (props.node !== props.selected) {
                props.onSelect(props.node);
                return;
            } props.onSelect(undefined); } }, props.node.id.toString()) : /*#__PURE__*/ _jsx(CategoryItem, { value: props.node, onClick: () => props.onSelect(props.node), selected: props.node === props.selected }), props.node.children.map(c => /*#__PURE__*/ _jsx(SearchItem, { showAll: props.showAll || match === "root", node: c, search: props.search, selected: props.selected, onSelect: props.onSelect }))] }); }
function Item(props) { const { selected, setSelected, tree } = props; let list = selected ? selected.children : tree.children; let parents = []; let cursor = selected; if (cursor && cursor.children.length === 0) {
    cursor = cursor.parent;
} while (cursor) {
    parents.push(cursor);
    cursor = cursor.parent;
} parents = parents.reverse(); return /*#__PURE__*/ _jsxs(_Fragment, { children: [parents.map((c, index) => /*#__PURE__*/ _jsx(ParentCategoryItem, { value: c, onClick: () => { if (c !== selected) {
                setSelected(c);
                return;
            } if (index > 0) {
                setSelected(parents[index - 1]);
                return;
            } setSelected(undefined); } }, c.id.toString())), selected && list.length === 0 && /*#__PURE__*/ _jsx(CategoryItem, { value: selected, selected: true }, selected.id.toString()), list.map(c => /*#__PURE__*/ _jsx(CategoryItem, { value: c, onClick: () => setSelected(c) }, c.id.toString()))] }); }
function ParentCategoryItem(props) { return /*#__PURE__*/ _jsxs(ListItemButton, { className: selectedCategoryClass, onClick: props.onClick, children: [/*#__PURE__*/ _jsx(ListItemText, { primary: props.value.name, style: { paddingRight: 16 } }), /*#__PURE__*/ _jsx(ExpandMoreIcon, {})] }, props.value.id.toString()); }
function CategoryItem(props) { const theme = useTheme(); if (props.selected) {
    return /*#__PURE__*/ _jsx("div", { style: { border: "2px solid " + theme.palette.primary.light }, children: /*#__PURE__*/ _jsx(ListItemButton, { children: /*#__PURE__*/ _jsx(ListItemText, { primary: props.value.name }) }) });
} return /*#__PURE__*/ _jsx(ListItemButton, { onClick: props.onClick, children: /*#__PURE__*/ _jsx(ListItemText, { primary: props.value.name }) }, props.value.id.toString()); }
const selectedCategoryClass = css({ "&.MuiListItemButton-root": { backgroundColor: grey["200"], border: "1px solid " + grey["300"] } });
const listClass = css({ "& .MuiListItemText-root": { whiteSpace: "nowrap" } });
